import { Key, ReactElement, forwardRef, useRef, useImperativeHandle, memo, useMemo } from 'react';
import DataGrid, { DataGridHandle, DataGridProps, RenderRowProps, Row } from 'components/DataGrid';
import { calendarWidthVar, grid, gridGroup, gridRow, scaleImageVar } from './style.css';
import { useColumns } from './hooks/useColumns';
import { usePlanClipboard } from './hooks/usePlanClipboard';
import type { GridSummaryRow, EntityKey, RowOrGroup, PlanResolution } from '../types';
import { Alert, Box } from '@ff-it/ui';
import { useDragMonitor } from './useDragAndDrop';
import { useSetAtom } from 'jotai';
import { collapsedGroupsAtom, useGridRows, useGridSelection, usePlanMutations } from '../state';
import { useFocusInvalid } from './hooks/useFocusInvalid';
import { useGridSummaryRows } from '../state';
import { useCalendar } from './calendar';
import { CalendarColumn } from './calendar/types';
import { DPR } from './calendar/Canvas';
import { INNER_ROW_HEIGHT } from './calendar/const';
import { isWeekend } from 'date-fns';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { PlanGridPopoverRoot } from './PlanGridPopover';
const HEADER_HEIGHT = 38;
const ROW_HEIGHT = 40;
const GROUP_HEIGHT = 30;
const SUMMARY_HEIGHT = 32;

const rowKeyGetter = (r: RowOrGroup): EntityKey => r._key;

export type GridHandle = {
  selectCell: (rowKey: string, columnKey?: string, enableEditor?: boolean, scrollToCell?: boolean) => void;
  // selectCell: (position: Position, enableEditor?: boolean, scrollToCell?: boolean) => void;
  focusInvalid: () => void;
};

function EmptyRowsRenderer(): ReactElement {
  return (
    <Box p="3" style={{ gridColumn: '1/-1' }}>
      <Alert m="0">This plan is empty</Alert>
    </Box>
  );
}

const rowClass = (row: RowOrGroup, _rowIdx: number): string => (row._isGroup ? gridGroup : gridRow);

const rowHeight = (row: RowOrGroup): number => (row._isGroup ? GROUP_HEIGHT : ROW_HEIGHT);

function createScaleImage(scale: CalendarColumn[], resolution: PlanResolution, width: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = width * DPR;
  canvas.height = INNER_ROW_HEIGHT * DPR;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(DPR, DPR);
  ctx.fillStyle = '#EAECF0'; // border color between columns
  ctx.fillRect(0, 0, width, INNER_ROW_HEIGHT);
  for (const { left, width: colWidth, start } of scale) {
    ctx.fillStyle = resolution === 'DAY' && isWeekend(start) ? '#F2F4F7' : '#ffffff';
    ctx.fillRect(left, 0, colWidth, INNER_ROW_HEIGHT);
  }
  return canvas.toDataURL();
}

type GridProps = Pick<DataGridProps<RowOrGroup, GridSummaryRow>, 'onSelectedCellChange' | 'presenceMarkers'>;

/**
 * Grid mangles PlanRow[] into flat set of RowOrGroup[]
 */
export const Grid = memo(
  forwardRef<GridHandle, GridProps>(({ onSelectedCellChange, presenceMarkers }: GridProps, ref): ReactElement => {
    const gridRef = useRef<DataGridHandle>(null);

    const setCollapsed = useSetAtom(collapsedGroupsAtom);

    const onRowsChange = usePlanMutations().onRowsChange;
    useDragMonitor(gridRef);

    const [selectedRows, onSelectedRowsChange] = useGridSelection();
    const calendar = useCalendar();
    const columns = useColumns(calendar);
    const clipboardProps = usePlanClipboard();

    const rows = useGridRows();
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const summaryRows = useGridSummaryRows();
    const triggerFocusInvalid = useFocusInvalid(gridRef, rows, columns);

    useImperativeHandle(ref, () => {
      const selectCell = (rowKey: string, columnKey?: string, enableEditor?: boolean, scrollToCell?: boolean): void => {
        requestAnimationFrame(() => {
          const rowIdx = rowsRef.current.findIndex((r) => r._key === rowKey);
          const idx = columnKey ? columns.findIndex((r) => r.key === columnKey) : 0;

          if (rowIdx === -1) {
            // ? Might not be expanded
          } else {
            gridRef.current?.selectCell({ rowIdx, idx }, enableEditor, scrollToCell);
          }
        });
      };

      const handle = {
        selectCell,
        focusInvalid: () => {
          // Make sure everything is expanded
          setCollapsed(new Set<EntityKey>());
          triggerFocusInvalid();
        },
      };
      window.__GRID__ = handle;
      return handle;
    });

    const rowsCount = rows.length + 2;

    function renderRow(key: Key, props: RenderRowProps<RowOrGroup, GridSummaryRow>): ReactElement {
      const row = props.row;
      return (
        <Row
          {...props}
          key={key}
          data-testid={key as string}
          data-rowkind={row._isGroup ? 'group' : row.kind}
          data-depth={row.depth}
          isRowSelected={row.isSelectable ? props.isRowSelected : false}
          aria-selected={row.isSelectable ? props['aria-selected'] : false}
        />
      );
    }
    const scaleImage = useMemo(() => createScaleImage(calendar.scale, calendar.resolution, calendar.width), [calendar]);
    return (
      <PlanGridPopoverRoot>
        <DataGrid<RowOrGroup, GridSummaryRow, EntityKey>
          ref={gridRef}
          rows={rows}
          aria-rowcount={rowsCount}
          rowHeight={rowHeight}
          headerRowHeight={HEADER_HEIGHT}
          summaryRowHeight={SUMMARY_HEIGHT}
          columns={columns}
          selectedRows={selectedRows}
          onSelectedRowsChange={onSelectedRowsChange}
          presenceMarkers={presenceMarkers}
          rowKeyGetter={rowKeyGetter}
          className={grid}
          style={assignInlineVars({
            [scaleImageVar]: `url(${scaleImage})`,
            [calendarWidthVar]: `${calendar.width}px`,
          })}
          // just edit on clik if possible
          onCellClick={({ selectCell, column }, e): void => {
            e.preventGridDefault();
            // HACK: for composite cell
            selectCell(column.key !== 'title');
          }}
          onCellDoubleClick={(_, e) => {
            e.preventGridDefault();
          }}
          onRowsChange={onRowsChange}
          bottomSummaryRows={summaryRows}
          renderRow={renderRow}
          noRowsFallback={<EmptyRowsRenderer />}
          rowClass={rowClass}
          deselectOnBlur
          onSelectedCellChange={onSelectedCellChange}
          {...clipboardProps}
        />
      </PlanGridPopoverRoot>
    );
  }),
);
